import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { CommunityService } from '../../services/community.service';

@Injectable({ providedIn: 'root' })
export class DomainService {
  private readonly hostname = window.location.hostname;

  isCommunityDomain(): boolean {
    return (
      this.hostname.includes('community.staging.connect.kontactapp.com') ||
      this.hostname.includes('community.connect.kontactapp.com') ||
      this.hostname.includes('domain-a.local')
    );
  }

  isProfileDomain(): boolean {
    return (
      this.hostname.includes('profile.staging.connect.kontactapp.com') ||
      this.hostname.includes('profiles.connect.kontactapp.com') ||
      this.hostname.includes('kon.tc') ||
      this.hostname.includes('domain-b.local')
    );
  }

  getCurrentDomainType(): 'community' | 'profile' | 'unknown' {
    if (this.isCommunityDomain()) return 'community';
    if (this.isProfileDomain()) return 'profile';
    return 'unknown';
  }

  private slugSub = new BehaviorSubject<string | null>(null);
  private communityIdSub = new BehaviorSubject<number | null>(null);
  private spaceIdSub = new BehaviorSubject<number | null>(null);
  private readySub = new BehaviorSubject<boolean>(false);

  slug$ = this.slugSub.asObservable();
  communityId$ = this.communityIdSub.asObservable();
  spaceID$ = this.spaceIdSub.asObservable();
  ctxReady$ = this.readySub.asObservable();

  getSlugSync(): string | null { return this.slugSub.value; }
  getCommunityIdSync(): number | null { return this.communityIdSub.value; }
  getSpaceIdSync(): number | null { return this.spaceIdSub.value; }
  isContextReadySync(): boolean { return this.readySub.value; }

  constructor(
    private router: Router,
    private communityApi: CommunityService
  ) {
    const lsSlug = localStorage.getItem('slug');
    const lsCommunityId = Number(localStorage.getItem('communityId') || '0') || null;
    let lsSpaceId = Number(localStorage.getItem('spaceID') || '0') || null;
    if (!lsSpaceId) {
      const legacySpaceId = Number(localStorage.getItem('spaceID') || '0') || null;
      if (legacySpaceId) {
        lsSpaceId = legacySpaceId;
        localStorage.setItem('spaceID', String(legacySpaceId));
      }
    }

    if (lsSlug && lsCommunityId) {
      this.slugSub.next(lsSlug);
      this.communityIdSub.next(lsCommunityId);
      if (lsSpaceId) this.spaceIdSub.next(lsSpaceId);
      this.readySub.next(!!lsSpaceId);
    }

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(async (e) => {
        const { slug, communityId } = this.extractFromUrl(e.urlAfterRedirects);
        if (slug && communityId) {
          await this.bootstrap(slug, communityId);
        }
      });
  }

  async bootstrap(slug: string, communityId: number): Promise<void> {
    const sameTarget =
      this.slugSub.value === slug &&
      this.communityIdSub.value === communityId &&
      !!this.spaceIdSub.value;

    if (sameTarget) {
      this.readySub.next(true);
      return;
    }

    this.readySub.next(false);
    this.slugSub.next(slug);
    this.communityIdSub.next(communityId);

    try {
      const res = await firstValueFrom(this.communityApi.getCommunityBySlug(slug));
      const spaceID = Number(res?.data?.space_id) || null;

      if (spaceID) {
        localStorage.setItem('slug', slug);
        localStorage.setItem('communityId', String(communityId));
        localStorage.setItem('spaceID', String(spaceID));
        this.spaceIdSub.next(spaceID);
        this.readySub.next(true);
      } else {
        this.readySub.next(false);
      }
    } catch {
      this.readySub.next(false);
    }
  }

  async setContext(slug: string, communityId: number, spaceID?: number | null): Promise<void> {
    if (spaceID) {
      localStorage.setItem('slug', slug);
      localStorage.setItem('communityId', String(communityId));
      localStorage.setItem('spaceID', String(spaceID));
      this.slugSub.next(slug);
      this.communityIdSub.next(communityId);
      this.spaceIdSub.next(spaceID);
      this.readySub.next(true);
      return;
    }
    await this.bootstrap(slug, communityId);
  }

  private extractFromUrl(url: string): { slug: string | null; communityId: number | null } {
    const clean = url.split('?')[0].split('#')[0];
    const parts = clean.split('/').filter(Boolean);
    if (parts.length >= 2) {
      const maybeSlug = parts[0];
      const maybeId = Number(parts[1]);
      if (maybeSlug && Number.isFinite(maybeId)) {
        return { slug: maybeSlug, communityId: maybeId };
      }
    }
    return { slug: null, communityId: null };
  }
}
